import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const emptyGst = {
  gst_number: '',
  state: '',
  state_code: '',
  address: '',
  pincode: '',
  pan_no: '',
  cin_no: '',
  accountNo: '',
  ifscCode: '',
  bankName: '',
  bankAddress: '',
  accountHolderName: '',
  branch: '',
  bankstate: '',
  branchName: '',
  branchCode: '',
  email: '', // Added email
  phone: '', // Added phone
};

const CompanyForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    business_name: '',
    entity_type: '',
    registration_type: '',
    nature_of_business: '',
    ad_code: '',
    iec_code: '',
    fssai_no: '', // Added FSSAI No field
    gst_details: [ { ...emptyGst } ],
  });
  const [originalFormData, setOriginalFormData] = useState({
    business_name: '',
    entity_type: '',
    registration_type: '',
    nature_of_business: '',
    ad_code: '',
    iec_code: '',
    fssai_no: '',
    gst_details: [ { ...emptyGst } ],
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch Company data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching Company with ID:', id);
      const fetchCompany = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/company-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch Company');
          }
          const result = await res.json();
          console.log('Fetched Company data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            
            // Handle GST details data
            let gstDetails = [];
            if (result.data.gst_details && Array.isArray(result.data.gst_details)) {
              gstDetails = result.data.gst_details.map(gst => ({
                gst_number: gst.gst_number ?? '',
                state: gst.state ?? '',
                state_code: gst.state_code ?? '',
                address: gst.address ?? '',
                pincode: gst.pincode ?? '',
                pan_no: gst.pan_no ?? '',
                cin_no: gst.cin_no ?? '',
                accountNo: gst.accountNo ?? '',
                ifscCode: gst.ifscCode ?? '',
                bankName: gst.bankName ?? '',
                bankAddress: gst.bankAddress ?? '',
                accountHolderName: gst.accountHolderName ?? '',
                branch: gst.branch ?? '',
                bankstate: gst.bankstate ?? '',
                branchName: gst.branchName ?? '',
                branchCode: gst.branchCode ?? '',
                email: gst.email ?? '',
                phone: gst.phone ?? ''
              }));
            }
            
            // If no GST details, add one empty GST detail
            if (gstDetails.length === 0) {
              gstDetails = [{ ...emptyGst }];
            }
            
            const newFormData = {
              business_name: result.data.business_name ?? '',
              entity_type: result.data.entity_type ?? '',
              registration_type: result.data.registration_type ?? '',
              nature_of_business: result.data.nature_of_business ?? '',
              ad_code: result.data.ad_code ?? '',
              iec_code: result.data.iec_code ?? '',
              fssai_no: result.data.fssai_no ?? '',
              gst_details: gstDetails
            };
            console.log('Setting new form data with GST details:', newFormData);
            setFormData(newFormData);
            // Store original values for read-only check - ensure we have a deep copy
            const originalData = JSON.parse(JSON.stringify(newFormData));
            setOriginalFormData(originalData);
          } else {
            toast.error('Company not found');
            navigate('/company');
          }
        } catch (error) {
          console.error('Error fetching Company:', error);
          toast.error('Failed to load Company data');
          navigate('/company');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchCompany();
    }
  }, [id, isEditMode, navigate]);

  // Helper function to check if field is read-only
  const isReadOnly = (fieldName) => {
    if (!isEditMode) return false;
    const originalValue = originalFormData[fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };

  // Helper function to check if GST field is read-only
  const isGstReadOnly = (fieldName, gstIndex) => {
    if (!isEditMode) return false;
    const originalGstDetails = originalFormData.gst_details;
    if (!originalGstDetails || !originalGstDetails[gstIndex]) return false;
    const originalValue = originalGstDetails[gstIndex][fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };

  // Field limits based on schema
  const FIELD_LIMITS = {
    business_name: 255,
    entity_type: 255,
    registration_type: 255,
    nature_of_business: 255,
    ad_code: 45,
    iec_code: 45,
    fssai_no: 45,
  };
  const GST_FIELD_LIMITS = {
    gst_number: 50,
    state: 255,
    state_code: 10,
    address: 1000, // text, UI limit
    pincode: 10,
    pan_no: 50,
    cin_no: 45,
    accountNo: 50,
    ifscCode: 20,
    bankName: 100,
    bankAddress: 1000, // text, UI limit
    accountHolderName: 100,
    branch: 100,
    bankstate: 100,
    branchName: 100,
    branchCode: 50,
    email: 45,
    phone: 20,
  };

  const validateField = (name, value, limits) => {
    let error = '';
    const limit = limits[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace(/_/g, ' ')}`;
    }
    // Required fields (only business_name is required in main form)
    if (name === 'business_name' && (!value || value.trim() === '')) {
      error = 'Business name is required';
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value, FIELD_LIMITS);
    if (error) toast.error(error);
  };

  const handleGstBlur = (idx, e) => {
    const { name, value } = e.target;
    const error = validateField(name, value, GST_FIELD_LIMITS);
    if (error) toast.error(`GST Row ${idx + 1}: ${error}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGstChange = (idx, e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only GST fields
    if (isGstReadOnly(name, idx)) {
      return;
    }
    
    setFormData(prev => {
      const gst_details = prev.gst_details.map((gst, i) =>
        i === idx ? { ...gst, [name]: value } : gst
      );
      // If state changes, update state_code
      if (name === 'state') {
        const matched = stateList.find(s => s.name === value);
        gst_details[idx].state_code = matched ? matched.code : '';
      }
      return { ...prev, gst_details };
    });
  };

  const addGstRow = () => {
    setFormData(prev => ({ ...prev, gst_details: [ ...prev.gst_details, { ...emptyGst } ] }));
  };

  const removeGstRow = (idx) => {
    setFormData(prev => ({ ...prev, gst_details: prev.gst_details.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate main form fields
    for (const key in formData) {
      if (FIELD_LIMITS[key]) {
        const error = validateField(key, formData[key], FIELD_LIMITS);
        if (error) {
          toast.error(error);
          setLoading(false);
          return;
        }
      }
    }
    
    // Validate GST details
    for (let i = 0; i < formData.gst_details.length; i++) {
      const gst = formData.gst_details[i];
      for (const key in gst) {
        if (GST_FIELD_LIMITS[key]) {
          const error = validateField(key, gst[key], GST_FIELD_LIMITS);
          if (error) {
            toast.error(`GST Row ${i + 1}: ${error}`);
            setLoading(false);
            return;
          }
        }
      }
    }
    
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/company-put/${id}`
        : `${API_BASE_URL}/api/masters/company-post`;
      
      console.log('=== SUBMITTING COMPANY DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Company Updated Successfully!' : 'Company Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            business_name: '', entity_type: '', registration_type: '', nature_of_business: '', ad_code: '', iec_code: '', fssai_no: '', gst_details: [ { ...emptyGst } ],
          });
        } else {
          navigate('/company');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b pb-3 mb-5">
            <div className="flex items-center gap-4">
              <h2 className="text-[15px] font-medium text-gray-800">{isEditMode ? 'Edit Company' : 'Company Registration'}</h2>
              {isEditMode && (
                <div className="text-[12px] text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/company')}
              className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-3 py-1 text-[12px] rounded font-normal hover:bg-[#166534] hover:text-white hover:border-[#166534] transition"
            >
              <FaArrowLeft />
              Back to List
            </button>
          </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Business Name</label>
            <input type="text" name="business_name" value={formData.business_name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.business_name} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" required autoComplete="off" />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Entity Type</label>
            <input type="text" name="entity_type" value={formData.entity_type} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.entity_type} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Registration Type</label>
            <input type="text" name="registration_type" value={formData.registration_type} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.registration_type} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">Nature of Business</label>
            <input type="text" name="nature_of_business" value={formData.nature_of_business} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.nature_of_business} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
            <div>
            <label className="block text-[13px] text-gray-700 mb-1">AD Code</label>
            <input type="text" name="ad_code" value={formData.ad_code} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.ad_code} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
            <div>
            <label className="block text-[13px] text-gray-700 mb-1">IEC CODE</label>
            <input type="text" name="iec_code" value={formData.iec_code} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.iec_code} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
          <div>
            <label className="block text-[13px] text-gray-700 mb-1">FSSAI No</label>
            <input type="text" name="fssai_no" value={formData.fssai_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.fssai_no} className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
          </div>
        </div>
        <div className="pt-4">
          <h3 className="text-md font-semibold mb-2">GST and Bank Details</h3>
          {formData.gst_details.map((gst, idx) => (
            <div key={idx} className="border p-3 mb-3 rounded bg-gray-50 relative">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Account Number</label>
                  <input type="text" name="accountNo" value={gst.accountNo} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.accountNo} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                    <div>
                  <label className="block text-[12px] text-gray-700 mb-1">IFSC Code</label>
                  <input type="text" name="ifscCode" value={gst.ifscCode} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.ifscCode} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                    <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Bank Name</label>
                  <input type="text" name="bankName" value={gst.bankName} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.bankName} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                    <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Bank Address</label>
                  <input type="text" name="bankAddress" value={gst.bankAddress} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.bankAddress} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                    <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Account Holder</label>
                  <input type="text" name="accountHolderName" value={gst.accountHolderName} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.accountHolderName} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                  <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Branch</label>
                  <input type="text" name="branch" value={gst.branch} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.branch} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                        <label className="block text-[12px] text-gray-700 mb-1">Bank State</label>
                       <select name="bankstate" value={gst.bankstate} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none">

                          <option value="">-- Select State --</option>
                          {stateList.map((s, i) => (
                            <option key={i} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                         <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Branch Name</label>
                  <input type="text" name="branchName" value={gst.branchName} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.branchName} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                   <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Branch Code</label>
                  <input type="text" name="branchCode" value={gst.branchCode} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.branchCode} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">GST Number</label>
                  <input type="text" name="gst_number" value={gst.gst_number} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.gst_number} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">State</label>
                  <select name="state" value={gst.state} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none">
                    <option value="">-- Select State --</option>
                    {stateList.map((s, i) => (
                      <option key={i} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">State Code</label>
                  <input type="text" name="state_code" value={gst.state_code} readOnly maxLength={GST_FIELD_LIMITS.state_code} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-gray-100 focus:outline-none rounded-none" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Address</label>
                  <input type="text" name="address" value={gst.address} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.address} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Pincode</label>
                  <input type="text" name="pincode" value={gst.pincode} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.pincode} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">PAN No</label>
                  <input type="text" name="pan_no" value={gst.pan_no} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.pan_no} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">CIN No</label>
                  <input type="text" name="cin_no" value={gst.cin_no} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.cin_no} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Email</label>
                  <input type="email" name="email" value={gst.email} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.email} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-[12px] text-gray-700 mb-1">Phone</label>
                  <input type="text" name="phone" value={gst.phone} onChange={e => handleGstChange(idx, e)} onBlur={e => handleGstBlur(idx, e)} maxLength={GST_FIELD_LIMITS.phone} className="w-full border border-gray-300 px-2 py-1 text-[12px] bg-white focus:outline-none focus:border-blue-400 rounded-none" autoComplete="off" />
                </div>
              </div>
              {formData.gst_details.length > 1 && (
                <button type="button" onClick={() => removeGstRow(idx)} className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-[12px] flex items-center gap-1"><FaTrash /> Remove</button>
              )}
            </div>
          ))}
          <button type="button" onClick={addGstRow} className="flex items-center gap-1 bg-gray-200 text-gray-800 px-2 py-1 text-[12px] rounded hover:bg-gray-300 border border-gray-300 font-medium mb-2"><FaPlus /> Add GST</button>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-3 py-1.5 text-[12px] bg-[#15803d] text-white font-normal rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60" style={{ minWidth: '110px' }} disabled={loading}>
            {loading ? 'Saving...' : (isEditMode ? 'Update Company' : 'Save Company')}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default CompanyForm;
